/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import cx from 'classnames';

import { SecurityHelper } from 'sarsaparilla';

import { Logo } from 'site-kit';
import { Freedom250LogoWrapper } from './Freedom250/Freedom250LogoWrapper';

import type { ReduxStore } from './Navbar.types';
import {
    GUEST_LOGIN_ALLOWED_PATH,
    useIsStickyStuck,
    useLogoutGuestOnDisallowedRoute,
} from './Navbar.utils';

import styles from './Navbar.module.scss';

import { NavbarRight } from './NavbarRight';
import { WidgetNavbar } from './widget/WidgetNavbar';

interface NavbarProps {
    isGuestAllowed?: boolean;
}

export function Navbar({ isGuestAllowed }: NavbarProps) {
    // State and refs

    const scrollCanaryRef = React.useRef<HTMLDivElement>(null);

    // Redux
    const auth = useSelector((state: ReduxStore) => state.auth);

    // Other hooks
    const { pathname } = useLocation();
    const isStuck = useIsStickyStuck(scrollCanaryRef.current);
    const isWidget = SecurityHelper.isWidgetSession();
    useLogoutGuestOnDisallowedRoute(isGuestAllowed);

    const isGuestLoginAllowed = isGuestAllowed && GUEST_LOGIN_ALLOWED_PATH.test(pathname);

    return (
        <>
            {/* This "scroll canary" div has an intersection observer attached to it so we
            know when to add the "has-shadow" class to the header. We don't put the observer
            on the header itself because to get it to work we would have to add a top: -1px
            which would cause the header to move slightly on scroll. The absolute positioning
            is required so it doesn't push the header down 1px */}
            <div
                ref={scrollCanaryRef}
                style={{ position: 'absolute', height: '1px' }}
                aria-hidden="true"
            />

            <header
                id="nav-header-container"
                className={cx(styles.header, {
                    [styles.hasShadow]: isStuck,
                })}
            >
                <div className={styles.headerContent}>
                    {isWidget ? (
                        <WidgetNavbar isGuestLoginAllowed={isGuestLoginAllowed} />
                    ) : (
                        <>
                            <div className={styles.leftContent}>
                                <div className={styles.logoHolder}>
                                    <div className={styles.logoAndBadge}>
                                        <a
                                            href="/"
                                            aria-label={`${process.env.SITE_NAME} Homepage`}
                                            id="ga-global-nav-logo-link"
                                            className={styles.logoLink}
                                            data-ga-tracking-id="812906380211"
                                            data-ga-tracking-value={`${process.env.SITE_NAME} Global Nav Icon`}
                                        >
                                            <Logo />
                                        </a>

                                        {auth?.account?.account?.is_commercial_account ? (
                                            <div className={styles.commercialAccount}>
                                                Commercial
                                            </div>
                                        ) : null}
                                    </div>

                                    <Freedom250LogoWrapper />
                                </div>
                            </div>

                            <NavbarRight isGuestLoginAllowed={isGuestLoginAllowed} />
                        </>
                    )}
                </div>
            </header>
        </>
    );
}
